import React, { useEffect, useCallback } from 'react';
import { X, Calendar, MapPin, Building2, Briefcase } from 'lucide-react';

const DetailModal = ({ event, onClose }) => {
  // Handle escape key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (event) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [event, handleKeyDown]);

  // Don't render if no event
  if (!event) return null;

  const getTrackStyles = () => {
    if (event.track === 'professional') {
      return {
        gradient: 'from-cyan-500 to-blue-600',
        bg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
        border: 'border-cyan-500',
        text: 'text-cyan-600',
        tagBg: 'bg-cyan-100',
        tagText: 'text-cyan-700',
        buttonHover: 'hover:bg-cyan-600'
      };
    }
    if (event.track === 'investments') {
      return {
        gradient: 'from-orange-500 to-red-500',
        bg: 'bg-gradient-to-br from-orange-500 to-red-500',
        border: 'border-orange-500',
        text: 'text-orange-600',
        tagBg: 'bg-orange-100',
        tagText: 'text-orange-700',
        buttonHover: 'hover:bg-orange-600'
      };
    }
    return {
      gradient: 'from-purple-500 to-pink-500',
      bg: 'bg-gradient-to-br from-purple-500 to-pink-500',
      border: 'border-purple-500',
      text: 'text-purple-600',
      tagBg: 'bg-purple-100',
      tagText: 'text-purple-700',
      buttonHover: 'hover:bg-purple-600'
    };
  };

  const styles = getTrackStyles();

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl animate-slideUp mx-2 sm:mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative background pattern - hidden on mobile */}
        <div className="hidden sm:block absolute top-0 right-0 w-64 h-64 opacity-10 pointer-events-none">
          <div className={`w-full h-full ${styles.bg} rounded-full blur-3xl`} />
        </div>

        {/* Header with gradient */}
        <div className={`${styles.bg} text-white p-4 sm:p-6 md:p-10 relative overflow-hidden rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl`}>
          {/* Animated background shapes - hidden on mobile */}
          <div className="hidden sm:block absolute inset-0 opacity-20 pointer-events-none">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-2xl animate-pulse" />
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>

          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 md:top-6 md:right-6 text-white/80 hover:text-white hover:bg-white/20 p-2 sm:p-2.5 md:p-3 rounded-full transition-colors duration-200 z-20 cursor-pointer"
            aria-label="Close modal"
            type="button"
            data-testid="detail-modal-close-btn"
          >
            <X size={24} className="sm:w-6 sm:h-6 md:w-7 md:h-7" />
          </button>
          
          <div className="relative z-10 pr-8 sm:pr-0">
            {/* Year and Track Badge */}
            <div className="text-[10px] sm:text-xs md:text-sm tracking-widest mb-1 sm:mb-2 md:mb-3 opacity-90 uppercase font-bold flex items-center gap-1.5 sm:gap-2">
              <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse" />
              {event.year} • {event.track}
            </div>
            
            {/* Title */}
            <h2 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight mb-1 sm:mb-2">
              {event.title}
            </h2>
            
            {/* Subtitle for professional */}
            {event.subtitle && (
              <p className="text-sm sm:text-base md:text-xl font-medium opacity-90 mb-1 sm:mb-2">
                {event.subtitle}
              </p>
            )}
            
            {/* Company / Platform / Milestone with metadata */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm md:text-base opacity-90">
              {event.company && (
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <Building2 size={14} className="sm:w-4 sm:h-4" />
                  {event.company}
                </span>
              )}
              {event.platform && (
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <Briefcase size={14} className="sm:w-4 sm:h-4" />
                  {event.platform}
                </span>
              )}
              {event.milestone && (
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <Calendar size={14} className="sm:w-4 sm:h-4" />
                  {event.milestone}
                </span>
              )}
              {event.location && (
                <span className="flex items-center gap-1 sm:gap-1.5">
                  <MapPin size={14} className="sm:w-4 sm:h-4" />
                  {event.location}
                </span>
              )}
              {event.duration && (
                <span className="flex items-center gap-1 sm:gap-1.5 bg-white/20 px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs">
                  {event.duration}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 md:p-10 space-y-4 sm:space-y-6 md:space-y-8 relative">
          {/* Overview / Description */}
          <div className="relative">
            <div className={`absolute -left-2 sm:-left-3 md:-left-4 top-0 w-0.5 sm:w-1 h-full ${styles.bg} rounded-full`} />
            <div className="pl-3 sm:pl-4 md:pl-8">
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4 flex items-center gap-2 sm:gap-3">
                <span className={`w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 ${styles.bg} rounded-full`} />
                Overview
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg">
                {event.description}
              </p>
            </div>
          </div>

          {/* Projects / Key Contributions - NEW SECTION */}
          {event.projects && event.projects.length > 0 && (
            <div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 sm:gap-3">
                <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${styles.bg} rounded-full`} />
                Key Projects &amp; Contributions
              </h3>
              <div className="space-y-3 sm:space-y-4">
                {event.projects.map((project, index) => (
                  <div 
                    key={index}
                    className={`p-3 sm:p-4 md:p-5 rounded-lg sm:rounded-xl border-l-4 ${styles.border} bg-gradient-to-r from-gray-50 to-white shadow-sm`}
                  >
                    <h4 className={`font-bold ${styles.text} text-sm sm:text-base md:text-lg mb-1.5 sm:mb-2`}>
                      {project.title}
                    </h4>
                    <ul className="space-y-1.5 sm:space-y-2">
                      {project.details.map((detail, idx) => (
                        <li key={idx} className="text-gray-600 text-xs sm:text-sm md:text-base flex items-start gap-1.5 sm:gap-2">
                          <span className={`mt-1 sm:mt-1.5 w-1 h-1 sm:w-1.5 sm:h-1.5 ${styles.bg} rounded-full flex-shrink-0`} />
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Details / Deep Dive */}
          {event.details && (
            <div className="bg-gray-50 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl md:rounded-2xl border-2 border-gray-200">
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 sm:gap-3">
                <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${styles.bg} rounded-full`} />
                Deep Dive
              </h3>
              <p className="text-gray-700 leading-relaxed text-xs sm:text-sm md:text-base">
                {event.details}
              </p>
            </div>
          )}

          {/* Professional specific - Skills */}
          {event.skills && event.skills.length > 0 && (
            <div>
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-2 sm:mb-3 md:mb-4 flex items-center gap-2 sm:gap-3">
                <span className={`w-1.5 h-1.5 sm:w-2 sm:h-2 ${styles.bg} rounded-full`} />
                Skills &amp; Technologies
              </h3>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 md:gap-3">
                {event.skills.map((skill, index) => (
                  <span
                    key={index}
                    className={`px-2 py-1 sm:px-3 sm:py-1.5 md:px-4 md:py-2 ${styles.tagBg} ${styles.tagText} text-[10px] sm:text-xs md:text-sm font-medium rounded-full 
                      border sm:border-2 ${styles.border} transition-all duration-300 hover:scale-105 hover:shadow-md cursor-default`}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Investment specific - Sector (NO AMOUNT) */}
          {event.sector && (
            <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border-2 ${styles.border} bg-gradient-to-br from-white to-gray-50`}>
              <h3 className="text-sm font-bold text-gray-500 mb-2 uppercase tracking-wide">Sector</h3>
              <p className={`text-xl md:text-2xl font-bold ${styles.text}`}>{event.sector}</p>
            </div>
          )}

          {event.thesis && (
            <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl border-l-4 ${styles.border} bg-gradient-to-r from-gray-50 to-white`}>
              <h3 className="text-base md:text-lg font-bold text-gray-800 mb-2 md:mb-3">Investment Thesis</h3>
              <p className="text-gray-700 leading-relaxed italic text-base md:text-lg">
                &ldquo;{event.thesis}&rdquo;
              </p>
            </div>
          )}

          {/* Personal specific - Impact */}
          {event.impact && (
            <div className={`p-4 md:p-6 rounded-xl md:rounded-2xl bg-gradient-to-br ${styles.gradient} text-white`}>
              <h3 className="text-base md:text-lg font-bold mb-2 md:mb-3">Impact</h3>
              <p className="leading-relaxed text-white/90 text-sm md:text-base">
                {event.impact}
              </p>
            </div>
          )}

          {/* Podcast links with full metadata */}
          {event.podcasts && event.podcasts.length > 0 && (
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-3">
                <span className={`w-2 h-2 ${styles.bg} rounded-full`} />
                Podcast Episodes
              </h3>
              <div className="space-y-4">
                {event.podcasts.map((podcast, index) => (
                  <a
                    key={index}
                    href={podcast.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-xl border border-slate-200 bg-white hover:border-slate-400 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 overflow-hidden group"
                  >
                    {/* Spotify green accent bar */}
                    <div className="h-1.5 bg-gradient-to-r from-[#1DB954] to-[#1ed760]" />
                    
                    <div className="p-4 md:p-5">
                      <div className="flex items-start gap-4">
                        {/* Spotify icon */}
                        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#1DB954] flex items-center justify-center text-white shadow-lg">
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                          </svg>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          {/* Title */}
                          <h4 className="font-bold text-slate-800 text-base md:text-lg mb-1 group-hover:text-slate-900 line-clamp-2">
                            {podcast.title}
                          </h4>
                          
                          {/* Description */}
                          {podcast.description && (
                            <p className="text-slate-600 text-sm mb-3 line-clamp-2 leading-relaxed">
                              {podcast.description}
                            </p>
                          )}
                          
                          {/* Metadata row */}
                          <div className="flex items-center gap-4 text-xs text-slate-500">
                            {podcast.duration && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {podcast.duration}
                              </span>
                            )}
                            {podcast.date && (
                              <span className="flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {podcast.date}
                              </span>
                            )}
                            <span className="ml-auto flex items-center gap-1 text-[#1DB954] font-semibold group-hover:gap-2 transition-all">
                              Listen
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 md:p-8 bg-gray-50 border-t-2 border-gray-200 rounded-b-2xl md:rounded-b-3xl">
          <button
            onClick={onClose}
            className={`w-full py-3 md:py-4 ${styles.bg} text-white font-bold rounded-xl ${styles.buttonHover} 
              transition-all duration-300 hover:shadow-xl transform hover:scale-[1.02] text-sm md:text-base`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailModal;
